import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ExternalLink, ChevronDown, ChevronUp, Shield, CheckCircle } from 'lucide-react';
import Logo from './Logo';
import { SAP_ICONS } from './sapIcons';

function SectionIcon({ section, className = 'w-6 h-6', color }) {
  if (section.icon_url) {
    return <img src={section.icon_url} alt="" className={`${className} object-contain`} />;
  }
  const Icon = SAP_ICONS[section.icon_name] || null;
  if (!Icon) return null;
  return <Icon className={className} style={color ? { color } : undefined} />;
}

function FAQItem({ item }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border border-[#E3DED5] rounded-xl overflow-hidden" data-testid="sap-faq-item">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-[#F9F6F0] transition-colors"
      >
        <span className="font-medium text-[#2A312C] pr-4">{item.question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-[#5C6A61] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#5C6A61] flex-shrink-0" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 bg-[#F9F6F0]">
          <p className="text-[#5C6A61] leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  );
}

function DynamicSection({ section }) {
  const accent = section.accent_color || '#A5BBA8';
  const boxStyle = section.bg_color ? { backgroundColor: section.bg_color, borderColor: `${accent}55` } : undefined;

  const Header = () => (
    <div className="flex items-start gap-4 mb-4">
      {(section.icon_url || section.icon_name) && (
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}22` }}>
          <SectionIcon section={section} color={accent} />
        </div>
      )}
      {section.title && (
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] pt-1.5">{section.title}</h2>
      )}
    </div>
  );

  if (section.type === 'callout') {
    return (
      <div className="rounded-2xl border p-6 md:p-8" style={boxStyle || { backgroundColor: '#F9F6F0', borderColor: '#E3DED5' }} data-testid={`sap-section-${section.id}`}>
        <Header />
        <div className="prose-sap text-[#3D4640] leading-relaxed" dangerouslySetInnerHTML={{ __html: section.body_html || '' }} />
      </div>
    );
  }

  if (section.type === 'list') {
    return (
      <div className="card-default" data-testid={`sap-section-${section.id}`}>
        <Header />
        <ul className="space-y-3">
          {(section.items || []).map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: accent }}>
                <CheckCircle className="w-3 h-3 text-white" />
              </div>
              <span className="text-[#3D4640]">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (section.type === 'steps') {
    return (
      <div className="card-default" data-testid={`sap-section-${section.id}`}>
        <Header />
        <div className="space-y-4">
          {(section.steps || []).map((step, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold" style={{ backgroundColor: accent }}>{i + 1}</div>
              <div className="flex-1 pt-1">
                <p className="font-semibold text-[#2A312C]">{step.title}</p>
                <p className="text-sm text-[#5C6A61]">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (section.type === 'faq') {
    const faq = [...(section.faq || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    return (
      <div data-testid={`sap-section-${section.id}`}>
        <Header />
        <div className="space-y-3">{faq.map((item, i) => <FAQItem key={i} item={item} />)}</div>
      </div>
    );
  }

  if (section.type === 'buttons') {
    return (
      <div className="card-default text-center bg-gradient-to-br from-[#F9F6F0] to-white" data-testid={`sap-section-${section.id}`}>
        {section.title && <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-6">{section.title}</h2>}
        <div className="flex flex-wrap justify-center gap-3">
          {(section.buttons || []).map((btn, i) => {
            const base = 'inline-flex items-center gap-2 px-6 py-3 rounded-xl transition-colors font-medium';
            const styles = {
              primary: 'bg-[#2A312C] text-white hover:bg-[#3D4640]',
              secondary: 'bg-[#A5BBA8] text-white hover:bg-[#8DA890]',
              outline: 'border border-[#2A312C] text-[#2A312C] hover:bg-[#2A312C] hover:text-white',
            };
            return (
              <a key={i} href={btn.url} target="_blank" rel="noopener noreferrer" className={`${base} ${styles[btn.style] || styles.primary}`} data-testid={`sap-link-${i}`}>
                <ExternalLink className="w-5 h-5" />{btn.text}
              </a>
            );
          })}
        </div>
      </div>
    );
  }

  if (section.type === 'image') {
    if (!section.image_url) return null;
    return (
      <div className="rounded-2xl overflow-hidden" data-testid={`sap-section-${section.id}`}>
        {section.title && <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-4">{section.title}</h2>}
        <img src={section.image_url} alt={section.title || ''} className="w-full object-cover rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="card-default" data-testid={`sap-section-${section.id}`}>
      <Header />
      <div className="prose-sap text-[#3D4640] leading-relaxed" dangerouslySetInnerHTML={{ __html: section.body_html || '' }} />
    </div>
  );
}

// Full public-page renderer, shared between the live page and the admin preview.
export default function SapPageRenderer({ content }) {
  if (!content) return null;

  const sections = [...(content.sections || [])]
    .filter((s) => s.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const HeroIcon = SAP_ICONS[content.hero_icon_name] || Shield;

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="sap-page">
      <header className="header-glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo /></Link>
          <div className="flex items-center gap-4">
            <Link to="/reserver" className="btn-primary text-sm hidden sm:inline-flex">Réserver</Link>
            <Link to="/" className="flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C] transition-colors">
              <ArrowLeft className="w-4 h-4" /><span className="hidden sm:inline">Accueil</span>
            </Link>
          </div>
        </div>
      </header>

      <section className="py-12 md:py-20 bg-gradient-to-b from-[#A5BBA8]/10 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {content.hero_badge_text && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#A5BBA8]/20 text-[#5C6A61] rounded-full mb-6">
              {content.hero_icon_url ? <img src={content.hero_icon_url} alt="" className="w-4 h-4 object-contain" /> : <HeroIcon className="w-4 h-4" />}
              <span className="text-sm font-medium">{content.hero_badge_text}</span>
            </div>
          )}
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl font-medium text-[#2A312C] mb-4" data-testid="sap-page-title">{content.page_title}</h1>
          {content.page_subtitle && <p className="text-lg text-[#5C6A61] max-w-2xl mx-auto mb-8">{content.page_subtitle}</p>}
          {content.show_sap_badge && content.sap_number && (
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-white border border-[#E3DED5] rounded-xl shadow-sm" data-testid="sap-number-badge">
              <CheckCircle className="w-6 h-6 text-[#A5BBA8]" />
              <span className="text-[#5C6A61]">{content.sap_declaration_text} <strong className="text-[#2A312C]">{content.sap_number}</strong></span>
            </div>
          )}
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {sections.map((section) => <DynamicSection key={section.id} section={section} />)}
        </div>
      </section>

      {content.show_cta && (
        <section className="py-12 bg-[#2A312C]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-medium text-white mb-4">{content.cta_title}</h2>
            {content.cta_text && <p className="text-[#A5BBA8] mb-8">{content.cta_text}</p>}
            <Link to={content.cta_button_link || '/reserver'} className="inline-flex items-center gap-2 px-8 py-4 bg-[#A5BBA8] text-white rounded-xl hover:bg-[#8DA890] transition-colors text-lg font-medium" data-testid="sap-cta-button">
              {content.cta_button_text}<ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      )}

      <footer className="py-8 bg-[#F9F6F0] border-t border-[#E3DED5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-block mb-4"><Logo variant="white" inverted={true} /></Link>
          <p className="text-sm text-[#5C6A61]">© {new Date().getFullYear()} Mitonner. Tous droits réservés.</p>
          {content.sap_number && <p className="text-xs text-[#5C6A61] mt-2">{content.sap_declaration_text} {content.sap_number}</p>}
        </div>
      </footer>
    </div>
  );
}
